import * as dayjs from "dayjs";
import * as ObjectSupport from "dayjs/plugin/objectSupport";
import * as IsSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as IsSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as Weekday from "dayjs/plugin/weekday";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import classNames from "classnames";
import { Schedule } from "Shared/models/Schedule";
import { html } from "lit-html";
import { useNumberFormatter } from "Services/useNumberFormatter";
import { useMemo, useState } from "Shared/haunted/CustomHooks";
import { DEFAULT_DATE_FORMAT } from "Services/constants";
import { TestIdDictionary as T } from "Services/test-ids/TestIdDictionary";
import { capitalizeAndRemoveDot } from "Services/common";
import i18next from "i18next";
import { calendarUtils } from "./calendarUtils";
dayjs.extend(CustomParseFormat);
dayjs.extend(ObjectSupport);
dayjs.extend(IsSameOrBefore);
dayjs.extend(IsSameOrAfter);
dayjs.extend(Weekday);


export interface Props {
	availableSchedules: Schedule[];
	culture: string;
	disabledDates: dayjs.Dayjs[];
	lowestDisplayedPrice: number;
	max: dayjs.Dayjs;
	min: dayjs.Dayjs;
	rangeStart: dayjs.Dayjs;
	selectedDate: dayjs.Dayjs;
	usePrices: boolean;
	onDateClick: (day: dayjs.Dayjs, e: MouseEvent) => void;
}

export const useNewFlightDateCalendar = (props: Props) => {
	const { doesWeekStartOnSunday } = calendarUtils();

	const { formatNumber } = useNumberFormatter();

	const [hoveredDay, setHoveredDay] = useState<dayjs.Dayjs>(undefined);

	// Helpers
	const weekdays = (culture: string) => {
		const days = [
			i18next.t("Lun"),
			i18next.t("Mar"),
			i18next.t("Mié"),
			i18next.t("Jue"),
			i18next.t("Vie"),
			i18next.t("Sáb"),
			i18next.t("Dom"),
		];

		if (doesWeekStartOnSunday(culture)) days.unshift(days.pop());

		return days;
	};

	const isDayInHoveredRange = (day: dayjs.Dayjs) =>
		props.rangeStart && day.isAfter(props.rangeStart, "day") && day.isBefore(hoveredDay, "day");

	const isDayDisabled = (day: dayjs.Dayjs) =>
		day.isBefore(props.min, "date") ||
		day.isAfter(props.max, "date") ||
		props.disabledDates.some((date) => date.isSame(day, "date"));

	const isDaySelected = (day: dayjs.Dayjs) =>
		[props.selectedDate, props.rangeStart].some((date) => date && date.isSame(day, "day"));

	const isDayInRange = (day: dayjs.Dayjs) =>
		props.rangeStart && props.selectedDate && day.isAfter(props.rangeStart, "day") && day.isBefore(props.selectedDate, "day");

	const findScheduleForDay = (day: dayjs.Dayjs | undefined, availableSchedules: Schedule[]): Schedule | undefined => {
		return day ? availableSchedules?.find((schedule) => schedule.departureDate.isSame(day, "day")) : undefined;
	};

	const formatSchedulePrice = (schedule: Schedule | undefined): string =>
		schedule && schedule.price
			? formatNumber({
					amount: schedule.price,
					currency: schedule.currency,
					culture: props.culture,
					leadingSign: true,
					forcedToRemoveDecimals: true,
			  }).replace(" ", "")
			: "";

	// Event handlers
	const handleMouseEnter = (day: dayjs.Dayjs) => {
		if (hoveredDay?.isSame(day, "day")) return;
		setHoveredDay(day);
	};

	const handleMouseLeave = (day: dayjs.Dayjs) => {
		if (!hoveredDay?.isSame(day, "day")) return;
		setHoveredDay(undefined);
	};

	const handleDateClick = (day: dayjs.Dayjs, e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (isDayDisabled(day)) return;
		props.onDateClick(day, e);
	};

	// Templates
	const weekdayHeaderTemplateMobile = (weekday: string) => html`
		<div
			class=${classNames(
				"relative inline-flex h-10 w-10 cursor-default flex-col items-center justify-center text-sm font-bold text-n-blue",
				"md:h-14 lg:w-[54px]"
			)}
		>
			${capitalizeAndRemoveDot(weekday)}
		</div>
	`;

	const weekdaysHeaderTemplateMobile = useMemo(
		() =>
			html`<div class=${classNames("grid grid-cols-7 w-full", "md:w-[280px] lg:w-[378px]")}>
				${weekdays(props.culture).map(weekdayHeaderTemplateMobile)}
			</div>`,
		[props.culture]
	);

	const weekTemplate = (week: dayjs.Dayjs[], isMobile: boolean) => html`
		<div
			class=${classNames("grid grid-cols-7", {
				"w-full": isMobile,
				"w-[280px] lg:w-[378px]": !isMobile,
			})}
		>
			${week.map((weekday) => dayTemplate(weekday, isMobile))}
		</div>
	`;

	const dayTemplate = (day: dayjs.Dayjs, isMobile: boolean) => {
		const currentSchedule = findScheduleForDay(day, props.availableSchedules);
		const price = formatSchedulePrice(currentSchedule);
		const isCheapest =
			props.usePrices && props.lowestDisplayedPrice && currentSchedule?.price === props.lowestDisplayedPrice;

		return day
			? html`
					<div
						class=${classNames(
							"relative inline-flex h-10 w-10 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-solid border-white text-base",
							"lg:h-11 lg:w-[54px]",
							{
								"bg-n-cyan text-white": isMobile
									? isDaySelected(day)
									: isDaySelected(day) && !isDayInHoveredRange(day),
								"bg-[#f2f2f2]": isMobile
									? isDayInRange(day)
									: (!hoveredDay && isDayInRange(day)) || isDayInHoveredRange(day),
								"md:hover:bg-n-cyan md:hover:text-white":
									!isMobile && !isDaySelected(day) && !isDayDisabled(day),
								"text-n-blue":
									!isDaySelected(day) && !isDayDisabled(day) && (!isMobile ? !isCheapest : true),
								"cursor-default text-[#c1c1c1]": isMobile
									? isDayDisabled(day)
									: isDayDisabled(day) && !isCheapest,
								"text-n-cyan":
									!isMobile && isCheapest && (!isDaySelected(day) || isDayInHoveredRange(day)),
								"text-white": !isMobile && isDaySelected(day) && !isDayInHoveredRange(day),
							}
						)}
						data-test-id=${T.Calendar.Date}
						data-test-value=${day.format(DEFAULT_DATE_FORMAT)}
						@mouseenter=${!isMobile ? () => handleMouseEnter(day) : undefined}
						@mouseleave=${!isMobile ? () => handleMouseLeave(day) : undefined}
						@click=${(e: MouseEvent) => handleDateClick(day, e)}
					>
						${day.format("DD")}
						${props.usePrices ? html`<span class="text-[8px]/none mt-0 md:mt-1 md:text-[10px]/none">${price}</span>` : ""}
					</div>
			  `
			: html`
					<div class=${classNames("relative inline-flex w-10", isMobile ? "h-10" : "h-[33px]")}>&nbsp;</div>
			  `;
	};

	// EXPORT
	const getCalendarForMonthTemplate = (calendarMonth: dayjs.Dayjs[][], isMobile: boolean) => {
		return html`<div class="mb-3 w-full">
			${weekdaysHeaderTemplateMobile} ${calendarMonth.map((week) => weekTemplate(week, isMobile))}
		</div>`;
	};

	return { getCalendarForMonthTemplate };
};
